import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faWandMagicSparkles, faUpload, faTrash } from '@fortawesome/pro-light-svg-icons';
import { BannerPickerService } from '../../banner-generator/banner-picker.service';
import { AuthService } from '../../../subscription/services/auth/auth.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'formly-field-banner',
  standalone: true,
  imports: [CommonModule, FormlyModule, FontAwesomeModule],
  template: `
    <div class="banner-field">
      <!-- Label with Action Buttons (right-aligned) -->
      <div class="d-flex align-items-center justify-content-between mb-1">
        <label class="form-label mb-0">{{ props.label || 'Banner' }}</label>
        <div class="d-flex align-items-center gap-2">
          <button
          type="button"
          class="btn btn-sm btn-link p-0"
          (click)="openPicker()"
          [disabled]="isUploading"
          title="Generate banner"
        >
          <fa-icon [icon]="icons.generate"></fa-icon>
        </button>
        <label class="btn btn-sm btn-link p-0 mb-0" [class.disabled]="isUploading" title="Upload image">
          <fa-icon [icon]="icons.upload"></fa-icon>
          <input
            type="file"
            accept="image/*"
            class="d-none"
            (change)="onFileSelected($event)"
            [disabled]="isUploading"
          />
        </label>
        @if (bannerUrl) {
          <button
            type="button"
            class="btn btn-sm btn-link p-0 text-danger"
            (click)="removeBanner()"
            [disabled]="isUploading"
            title="Remove banner"
          >
            <fa-icon [icon]="icons.trash"></fa-icon>
          </button>
        }
        @if (isUploading) {
          <span class="text-muted small">
            <span class="spinner-border spinner-border-sm me-1"></span>
          </span>
        }
        </div>
      </div>

      <!-- Banner Preview -->
      <div class="banner-preview-container">
        @if (bannerUrl) {
          <div class="banner-preview">
            <img [src]="displayUrl" alt="Banner" class="banner-image" />
          </div>
        } @else {
          <div class="banner-placeholder">
            <span class="text-muted small">No banner set</span>
          </div>
        }
      </div>

      @if (error) {
        <div class="text-danger small mt-2">{{ error }}</div>
      }
    </div>
  `,
  styles: [`
    .banner-preview {
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid var(--bs-border-color);
    }

    .banner-image {
      display: block;
      width: 100%;
      height: auto;
      aspect-ratio: 4 / 1;
      object-fit: cover;
    }

    .banner-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      border-radius: 8px;
      border: 2px dashed var(--bs-border-color);
      background: var(--bs-light);
      aspect-ratio: 4 / 1;
      max-height: 100px;
    }

    label.disabled {
      pointer-events: none;
      opacity: 0.65;
    }
  `],
})
export class FormlyBannerComponent extends FieldType<FieldTypeConfig> {
  private bannerPickerService = inject(BannerPickerService);
  private authService = inject(AuthService);

  icons = {
    generate: faWandMagicSparkles,
    upload: faUpload,
    trash: faTrash,
  };

  isUploading = false;
  uploadingMessage = 'Uploading...';
  error?: string;

  get bannerUrl(): string | null {
    return this.formControl.value || null;
  }

  get displayUrl(): string | null {
    const url = this.bannerUrl;
    if (!url) return null;
    return url.replace('/jaxfs/', environment.JAXFS_ROOT);
  }

  async openPicker(): Promise<void> {
    const result = await this.bannerPickerService.pick();

    if (result?.url) {
      this.formControl.setValue(result.url);
      this.formControl.markAsDirty();
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.error = 'Please select an image file';
      return;
    }

    // Validate file size (max 10MB for banners)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.error = 'Image must be less than 10MB';
      return;
    }

    this.isUploading = true;
    this.uploadingMessage = 'Uploading...';
    this.error = undefined;

    try {
      const url = await this.authService.uploadProfileAsset(file, 'banner');
      this.formControl.setValue(url);
      this.formControl.markAsDirty();
    } catch (err: unknown) {
      console.error('Banner upload error:', err);
      this.error = err instanceof Error ? err.message : 'Failed to upload image';
    } finally {
      this.isUploading = false;
      input.value = '';
    }
  }

  removeBanner(): void {
    this.formControl.setValue(null);
    this.formControl.markAsDirty();
    this.error = undefined;
  }
}
